/**
 * Public service for advisor invitation operations that don't require authentication
 * Used for accept/reject invitation flows
 */

import {
  AcceptAdvisorInvitationRequest,
  AcceptAdvisorInvitationResponse,
  AdvisorInvitationVerificationResponse,
  RejectAdvisorInvitationRequest,
  RejectAdvisorInvitationResponse,
} from '../models/Advisor';
import BaseBackendService from './BaseBackendService';

export class PublicAdvisorInvitationService extends BaseBackendService {
  private async publicFetchJson<T>(
    path: string,
    init: RequestInit,
    errorCode: string,
    defaultError: string,
  ): Promise<T> {
    try {
      const response = await fetch(this.apiURL(path), init);
      if (!response.ok) {
        const errorBody = await response.json();
        throw {
          code: errorCode,
          error: errorBody.error || defaultError,
          details: errorBody,
        };
      }
      return response.json();
    } catch (error: any) {
      if (error?.code && error?.error) {
        throw error;
      }
      throw {
        code: 'NETWORK_ERROR',
        error: `Network error while ${defaultError.toLowerCase()}`,
        details: error,
      };
    }
  }

  /**
   * Verify invitation validity (public endpoint)
   */
  async verifyAdvisorInvitation(
    invitationId: string,
  ): Promise<AdvisorInvitationVerificationResponse> {
    const json = await this.publicFetchJson<{
      data: AdvisorInvitationVerificationResponse;
      signature?: string;
    }>(
      `advisors/invitation/verify/${invitationId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
      'VERIFICATION_ERROR',
      'Failed to verify invitation',
    );
    return { ...json.data, signature: json.signature };
  }

  /**
   * Accept invitation (public endpoint)
   */
  async acceptAdvisorInvitation(
    invitationId: string,
    acceptanceData: AcceptAdvisorInvitationRequest,
  ): Promise<AcceptAdvisorInvitationResponse> {
    const json = await this.publicFetchJson<{ data: AcceptAdvisorInvitationResponse }>(
      `advisors/invitation/accept/${invitationId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acceptanceData),
      },
      'ACCEPTANCE_ERROR',
      'Failed to accept invitation',
    );
    return json.data;
  }

  /**
   * Reject invitation (public endpoint)
   */
  async rejectAdvisorInvitation(
    invitationId: string,
    rejectionData?: RejectAdvisorInvitationRequest,
  ): Promise<RejectAdvisorInvitationResponse> {
    const json = await this.publicFetchJson<{ data: RejectAdvisorInvitationResponse }>(
      `advisors/invitation/reject/${invitationId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rejectionData || {}),
      },
      'REJECTION_ERROR',
      'Failed to reject invitation',
    );
    return json.data;
  }
}

// Export singleton instance
export const publicAdvisorInvitationService = new PublicAdvisorInvitationService();
